import React, { useState, useRef, useEffect, useCallback } from 'react';
import { 
  VideoOff,
  MicOff,
  Phone,
  MonitorOff,
  MessageSquare,
  FileText,
  Save,
  Clock,
  Circle,
  Square,
  Send,
  Settings,
  Maximize,
  Minimize,
  Users,
  UserPlus,
  UserX,
  Copy,
  CheckCircle,
  ChevronRight
} from 'lucide-react';
import { useRealtimeKitClient } from '@cloudflare/realtimekit-react';
import { RtkMeeting } from '@cloudflare/realtimekit-react-ui';
import { BrandingProvider } from '../../contexts/BrandingContext';
import { BrandedButton, BrandedCard } from '../../components/branding/BrandedComponents';

interface GeneralAgentSessionProps {
  roomId?: string;
  agentName?: string;
  companyName?: string;
  requestId?: string;
  onEndSession?: () => void;
  onSaveNotes?: (notes: string) => void;
}

interface ChatMessage {
  id: string;
  sender: 'agent' | 'customer' | 'system';
  message: string;
  timestamp: Date;
  type: 'text' | 'file' | 'system';
}

interface Participant {
  id: string;
  name: string;
  role: 'agent' | 'customer' | 'observer';
  status: 'connected' | 'waiting' | 'disconnected';
  joinedAt: Date;
}

// Service to generate tokens
const generateUniversalToken = async (roomId: string, participantName: string, isHost: boolean = true) => {
  console.log(`[GeneralTokenService] Generating token for room ${roomId}, participant ${participantName}, host: ${isHost}`);
  
  try {
    // Use the working debug endpoint that universal template uses
    const response = await fetch('https://dev-platform-api-dev.benjiemalinao879557.workers.dev/client/v1/debug/generate-token', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        meetingId: roomId,
        participantName: participantName,
        isHost: isHost
      })
    });

    if (!response.ok) {
      throw new Error(`Token generation failed: ${response.status}`);
    }

    const data = await response.json();
    console.log(`[GeneralTokenService] Token generated successfully`);
    
    return {
      token: data.token,
      participantId: `participant-${Date.now()}`,
      roomId
    };
  } catch (error) {
    console.error('[GeneralTokenService] Error generating token:', error);
    throw error;
  }
};

// Inner component that uses RealtimeKit hooks
const RealtimeKitMeetingComponent: React.FC<{
  authToken: string;
  onEndCall: () => void;
  agentName: string;
  companyName: string;
  roomId: string;
}> = ({ authToken, onEndCall, agentName, companyName, roomId }) => {
  const [meeting, initMeeting] = useRealtimeKitClient();
  const [isInitialized, setIsInitialized] = useState(false);
  const [sessionTime, setSessionTime] = useState(0);
  const [isRecording, setIsRecording] = useState(false);
  const [showChat, setShowChat] = useState(false);
  const [showNotes, setShowNotes] = useState(true);
  const [showParticipants, setShowParticipants] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [copiedLink, setCopiedLink] = useState(false);
  const [sessionNotes, setSessionNotes] = useState('');
  const [newMessage, setNewMessage] = useState('');
  
  // Mock data
  const [participants, setParticipants] = useState<Participant[]>([
    {
      id: '1',
      name: agentName,
      role: 'agent',
      status: 'connected',
      joinedAt: new Date()
    }
  ]);

  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([
    {
      id: '1',
      sender: 'system',
      message: 'Session started. Customer will join shortly.',
      timestamp: new Date(),
      type: 'system'
    }
  ]);

  const chatEndRef = useRef<HTMLDivElement>(null);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  // Initialize RealtimeKit meeting
  useEffect(() => {
    const initializeSession = async () => {
      try {
        console.log('[GeneralAgentSession] Initializing meeting with token');
        
        await initMeeting({
          authToken: authToken,
          defaults: {
            audio: true,
            video: true,
          },
        });
        
        setIsInitialized(true);
        console.log('[GeneralAgentSession] Meeting initialized successfully');
      } catch (error) {
        console.error('[GeneralAgentSession] Failed to initialize meeting:', error);
      }
    };

    if (authToken) {
      initializeSession();
    }

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [authToken, initMeeting]);

  // Timer for session duration
  useEffect(() => {
    timerRef.current = setInterval(() => {
      setSessionTime(prev => prev + 1);
    }, 1000);

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, []);

  // Auto-scroll chat to bottom
  useEffect(() => {
    if (chatEndRef.current) {
      chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [chatMessages]);

  // Format session time
  const formatTime = useCallback((seconds: number): string => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    if (hrs > 0) {
      return `${hrs.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    } else {
      return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
  }, []);

  // Handle chat message send
  const sendMessage = useCallback(() => {
    if (newMessage.trim()) {
      const message: ChatMessage = {
        id: Date.now().toString(),
        sender: 'agent',
        message: newMessage.trim(),
        timestamp: new Date(),
        type: 'text'
      };
      setChatMessages(prev => [...prev, message]);
      setNewMessage('');
    }
  }, [newMessage]);

  // Handle copy invite link
  const copyInviteLink = useCallback(() => {
    const orgMatch = window.location.pathname.match(/\/(org_[a-zA-Z0-9_]+)\//);
    const orgPrefix = orgMatch ? `/${orgMatch[1]}` : '';
    const inviteLink = `${window.location.origin}${orgPrefix}/templates/general/session/participant?roomId=${roomId}`;
    
    navigator.clipboard.writeText(inviteLink);
    setCopiedLink(true);
    setTimeout(() => setCopiedLink(false), 2000);
  }, [roomId]);

  // Handle end session
  const handleEndSession = useCallback(() => {
    console.log('[GeneralAgentSession] Ending session');
    // Properly leave the meeting to end it for all participants
    if (meeting) {
      meeting.leave();
      console.log('[GeneralAgentSession] Meeting.leave() called - ending for all participants');
    }
    // Navigate back after a short delay
    setTimeout(() => {
      onEndCall();
    }, 500);
  }, [meeting, onEndCall]);

  if (!isInitialized) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center text-white">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-4"></div>
          <p>Initializing session...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 text-white">
      {/* Header */}
      <div className="bg-gray-800 border-b border-gray-700 p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <h1 className="text-xl font-semibold">{companyName} - Agent Session</h1>
            <div className="flex items-center space-x-2 text-green-400">
              <Circle className="w-3 h-3 fill-current" />
              <span className="text-sm">Live Session</span>
            </div>
          </div>
          
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2 text-gray-300">
              <Clock className="w-4 h-4" />
              <span className="font-mono">{formatTime(sessionTime)}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="flex h-screen">
        {/* Main Video Area */}
        <div className="flex-1 relative bg-black">
          <div className="h-full w-full relative">
            {meeting ? (
              <RtkMeeting 
                meeting={meeting} 
                showSetupScreen={false}
                mode="fill"
              />
            ) : (
              <div className="w-full h-full bg-gray-800 flex items-center justify-center">
                <div className="text-center text-white">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-4"></div>
                  <p>Connecting to video...</p>
                </div>
              </div>
            )}
          </div>
          
          {/* Floating End Call Button */}
          <div className="absolute bottom-6 left-1/2 transform -translate-x-1/2">
            <button
              onClick={handleEndSession}
              aria-label="End session"
              className="bg-red-600 text-white px-6 py-3 rounded-full hover:bg-red-700 transition-colors flex items-center space-x-2 shadow-lg"
            >
              <Phone className="w-5 h-5" />
              <span>End Session</span>
            </button>
          </div>
        </div>

        {/* Right Sidebar */}
        <div className="w-80 bg-gray-800 border-l border-gray-700 flex flex-col">
          {/* Sidebar Tabs */}
          <div className="flex border-b border-gray-700">
            <button
              onClick={() => {setShowChat(true); setShowNotes(false); setShowParticipants(false);}}
              className={`flex-1 py-3 px-4 text-sm font-medium ${showChat ? 'bg-gray-700 text-white' : 'text-gray-400 hover:text-white'}`}
            >
              <MessageSquare className="w-4 h-4 inline mr-2" />
              Chat
            </button>
            <button
              onClick={() => {setShowNotes(true); setShowChat(false); setShowParticipants(false);}}
              className={`flex-1 py-3 px-4 text-sm font-medium ${showNotes ? 'bg-gray-700 text-white' : 'text-gray-400 hover:text-white'}`}
            >
              <FileText className="w-4 h-4 inline mr-2" />
              Notes
            </button>
            <button
              onClick={() => {setShowParticipants(true); setShowChat(false); setShowNotes(false);}}
              className={`flex-1 py-3 px-4 text-sm font-medium ${showParticipants ? 'bg-gray-700 text-white' : 'text-gray-400 hover:text-white'}`}
            >
              <Users className="w-4 h-4 inline mr-2" />
              People
            </button>
          </div>

          {/* Chat Panel */}
          {showChat && (
            <div className="flex-1 flex flex-col">
              <div className="flex-1 overflow-y-auto p-4 space-y-3">
                {chatMessages.map((msg) => (
                  <div key={msg.id} className={`${msg.sender === 'system' ? 'text-center' : ''}`}>
                    {msg.sender === 'system' ? (
                      <div className="text-xs text-gray-500 bg-gray-700 rounded px-2 py-1 inline-block">
                        {msg.message}
                      </div>
                    ) : (
                      <div className={`${msg.sender === 'agent' ? 'text-right' : 'text-left'}`}>
                        <div className={`inline-block px-3 py-2 rounded-lg max-w-xs ${
                          msg.sender === 'agent' 
                            ? 'bg-blue-600 text-white' 
                            : 'bg-gray-700 text-white'
                        }`}>
                          <p className="text-sm">{msg.message}</p>
                          <p className="text-xs opacity-75 mt-1">
                            {msg.timestamp.toLocaleTimeString()}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
                <div ref={chatEndRef} />
              </div>
              
              <div className="p-4 border-t border-gray-700">
                <div className="flex space-x-2">
                  <input
                    type="text"
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
                    placeholder="Type a message..."
                    className="flex-1 bg-gray-700 text-white rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <button
                    onClick={sendMessage}
                    aria-label="Send message"
                    className="bg-blue-600 text-white rounded px-3 py-2 hover:bg-blue-700 transition-colors"
                  >
                    <Send className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Notes Panel */}
          {showNotes && (
            <div className="flex-1 flex flex-col p-4">
              <h3 className="text-lg font-medium mb-4">Session Notes</h3>
              <textarea
                value={sessionNotes}
                onChange={(e) => setSessionNotes(e.target.value)}
                placeholder="Add notes about this session..."
                className="flex-1 bg-gray-700 text-white rounded p-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
              />
              <button
                onClick={() => {/* Save notes logic */}}
                className="mt-3 bg-blue-600 text-white rounded px-4 py-2 hover:bg-blue-700 transition-colors flex items-center justify-center"
              >
                <Save className="w-4 h-4 mr-2" />
                Save Notes
              </button>
            </div>
          )}

          {/* Participants Panel */}
          {showParticipants && (
            <div className="flex-1 p-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium">Participants ({participants.length})</h3>
                <button
                  onClick={copyInviteLink}
                  className={`text-sm px-3 py-1 rounded transition-colors ${
                    copiedLink 
                      ? 'bg-green-600 text-white' 
                      : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                  }`}
                >
                  {copiedLink ? (
                    <>
                      <CheckCircle className="w-3 h-3 inline mr-1" />
                      Copied!
                    </>
                  ) : (
                    <>
                      <Copy className="w-3 h-3 inline mr-1" />
                      Copy Link
                    </>
                  )}
                </button>
              </div>
              
              <div className="space-y-2">
                {participants.map((participant) => (
                  <div key={participant.id} className="flex items-center justify-between p-3 bg-gray-700 rounded">
                    <div className="flex items-center space-x-3">
                      <div className={`w-3 h-3 rounded-full ${
                        participant.status === 'connected' ? 'bg-green-400' : 'bg-gray-500'
                      }`} />
                      <div>
                        <p className="text-sm font-medium">{participant.name}</p>
                        <p className="text-xs text-gray-400 capitalize">{participant.role}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Main component with token generation
export const GeneralAgentSession: React.FC<GeneralAgentSessionProps> = ({
  roomId: initialRoomId,
  agentName = 'Agent',
  companyName = 'Customer Connect',
  requestId,
  onEndSession,
  onSaveNotes
}) => {
  const [authToken, setAuthToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Get URL parameters
  const urlParams = new URLSearchParams(window.location.search);
  const roomId = initialRoomId || urlParams.get('roomId') || '';
  const agentNameFromUrl = urlParams.get('agentName') || agentName;
  const requestIdFromUrl = urlParams.get('requestId') || requestId || '';

  useEffect(() => {
    const initializeToken = async () => {
      if (!roomId) {
        setError('No room ID provided');
        setLoading(false);
        return;
      }

      try {
        console.log('[GeneralAgentSession] Generating token for room:', roomId);
        const tokenData = await generateUniversalToken(roomId, agentNameFromUrl, true);
        setAuthToken(tokenData.token);
        setLoading(false);
        console.log('[GeneralAgentSession] Token generated successfully');
      } catch (err) {
        console.error('[GeneralAgentSession] Failed to generate token:', err);
        setError('Failed to join session. Please try again.');
        setLoading(false);
      }
    };

    initializeToken();
  }, [roomId, agentNameFromUrl]);

  const handleEndCall = useCallback(() => {
    console.log('[GeneralAgentSession] Session ended');
    if (onEndSession) {
      onEndSession();
    } else {
      // Navigate back to dashboard
      const orgMatch = window.location.pathname.match(/\/(org_[a-zA-Z0-9_]+)\//);
      const orgPrefix = orgMatch ? `/${orgMatch[1]}` : '';
      window.location.href = `${orgPrefix}/templates/general/dashboard`;
    }
  }, [onEndSession]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center text-white">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-4"></div>
          <p>Joining session...</p>
          <p className="text-sm text-gray-400 mt-2">Room: {roomId}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center text-white max-w-md">
          <div className="bg-red-600 rounded-full p-4 mx-auto mb-4 w-16 h-16 flex items-center justify-center">
            <Phone className="w-8 h-8" />
          </div>
          <h1 className="text-2xl font-bold mb-2">Failed to Join Session</h1>
          <p className="text-gray-400 mb-6">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="bg-blue-600 text-white px-6 py-3 rounded hover:bg-blue-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (!authToken) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center text-white">
          <p>Preparing session...</p>
        </div>
      </div>
    );
  }

  return (
    <BrandingProvider templateType="general" initialBranding={{ companyName: companyName }}>
      <RealtimeKitMeetingComponent
        authToken={authToken}
        onEndCall={handleEndCall}
        agentName={agentNameFromUrl}
        companyName={companyName}
        roomId={roomId}
      />
    </BrandingProvider>
  );
};
